import { COLOR } from "@finway-group/shared/lib/consts"
import { BarDatum, ComputedBarDatum } from "@nivo/bar"
import { useTooltip } from "@nivo/tooltip"
import React from "react"

const MIN_BAR_HEIGHT = 2
const BAR_GAP = 1

interface CustomBarProps {
    bar: ComputedBarDatum<BarDatum>
    borderRadius: number
    borderWidth: number
    label: string
    shouldRenderLabel: boolean
    isInteractive: boolean
    onClick?: (data: ComputedBarDatum<BarDatum>["data"] & { color: string }, event: React.MouseEvent) => void
    onMouseEnter?: (data: ComputedBarDatum<BarDatum>["data"], event: React.MouseEvent) => void
    onMouseLeave?: (data: ComputedBarDatum<BarDatum>["data"], event: React.MouseEvent) => void
    tooltip: React.FC<{ id: string | number; value: number; color: string; label: string }>
    offsets: Map<string, number>
}

export const CustomBarComponent = ({
    bar,
    borderRadius,
    borderWidth,
    label,
    shouldRenderLabel,
    isInteractive,
    onClick,
    onMouseEnter,
    onMouseLeave,
    tooltip: TooltipComponent,
    offsets,
}: CustomBarProps) => {
    const { showTooltipFromEvent, hideTooltip } = useTooltip()

    const hasValue = bar.data.value && bar.data.value > 0

    if (!hasValue) {
        return null
    }

    const baseHeight = bar.height < MIN_BAR_HEIGHT ? MIN_BAR_HEIGHT : bar.height
    const height = baseHeight - BAR_GAP

    // Look up pre-calculated offset from the offsets map
    const barKey = `${bar.data.indexValue}-${bar.data.id}`
    const yOffset = offsets.get(barKey) || 0

    const handleMouseEnter = (event: React.MouseEvent<SVGRectElement>) => {
        if (!isInteractive) {
            return
        }
        showTooltipFromEvent(<TooltipComponent id={bar.data.id} color={bar.color} label={String(bar.data.id)} value={bar.data.value ?? 0} />, event)
        onMouseEnter?.(bar.data, event)
    }

    const handleMouseLeave = (event: React.MouseEvent<SVGRectElement>) => {
        if (!isInteractive) {
            return
        }
        hideTooltip()
        onMouseLeave?.(bar.data, event)
    }

    const handleClick = (event: React.MouseEvent<SVGRectElement>) => {
        if (!isInteractive) {
            return
        }
        onClick?.({ ...bar.data, color: bar.color }, event)
    }

    return (
        <g transform={`translate(${bar.x}, ${bar.y - yOffset})`}>
            <rect
                width={bar.width}
                height={height}
                fill={bar.color}
                rx={borderRadius}
                ry={borderRadius}
                strokeWidth={borderWidth}
                style={{ cursor: isInteractive ? "pointer" : "default" }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={handleClick}
            />
            {shouldRenderLabel && (
                <text
                    x={bar.width / 2}
                    y={height / 2}
                    textAnchor="middle"
                    dominantBaseline="central"
                    style={{ pointerEvents: "none", fontSize: 12, fontWeight: 600 }}
                    fill={COLOR.white}
                >
                    {label}
                </text>
            )}
        </g>
    )
}
